/*
 * Nexus Elite Solutions - 2nd October 2026
 *
 * Puts the Vitals widgets on the product page where they belong.
 *
 * 9th October 2026 - a THIRD widget is handled now: the trust badges. See
 * placeTrustBadges below.
 *
 * Vitals renders both of these in the browser after the page has loaded - they
 * are NOT in the server HTML, so the theme has no say in where they land, and
 * Liquid cannot place them. Left alone they arrive here:
 *
 *   .main-product--wrapper
 *     .main-product--desktop   <- media, desktop
 *     .main-product--mobile    <- media, mobile
 *     [Vitals bundles]         <- lands HERE: a third desktop column, and on
 *                                 mobile it sits above the product title
 *     form.product-form
 *       ...
 *       .product-buy-buttons--root
 *       [Vitals wishlist]      <- lands HERE: AFTER the buy buttons box rather
 *                                 than inside it, so it drops onto its own line
 *
 * This script moves each one once the DOM actually has it:
 *   - bundles  -> directly below the variant pickers, inside the product form
 *   - wishlist -> inside the buy buttons box, straight after ADD TO CART, which
 *                 is what the row layout in custom-styles.liquid expects
 *   - badges   -> after the last block in the form, below the whole accordion
 *
 * One DOM serves both breakpoints, so each widget ends up in the same place on
 * mobile and desktop.
 *
 * NO VITALS CLASS NAMES ARE GUESSED. The wishlist is matched on the one class
 * taken from its real markup; the bundles widget is found structurally instead,
 * as the one child of .main-product--wrapper that the theme does not render
 * itself. That also catches any other widget dropped in the same gap.
 *
 * Vitals may re-insert a widget after we move it, so a MutationObserver puts it
 * back. Every element gets at most MAX_MOVES moves, so if Vitals ever fights
 * back the two stop rather than looping forever.
 */
(function () {
  'use strict';

  var MAX_MOVES = 5;
  var moves = new WeakMap();

  function mayMove(el) {
    var count = moves.get(el) || 0;
    if (count >= MAX_MOVES) return false;
    moves.set(el, count + 1);
    return true;
  }

  /* The elements the theme itself puts inside .main-product--wrapper. Anything
     else in there was injected by an app. */
  var THEME_OWN = ['main-product--desktop', 'main-product--mobile', 'product--header'];
  var IGNORED_TAGS = ['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'LINK'];

  function isThemeOwn(el) {
    if (IGNORED_TAGS.indexOf(el.tagName) !== -1) return true;
    if (el.tagName === 'FORM' && el.classList.contains('product-form')) return true;

    for (var i = 0; i < THEME_OWN.length; i++) {
      if (el.classList.contains(THEME_OWN[i])) return true;
    }

    return false;
  }

  function placeStrays(wrapper) {
    var form = wrapper.querySelector('form.product-form');
    if (!form) return;

    var anchor = form.querySelector('.product-form--block[data-block-type="options"]');
    if (!anchor) return;

    var children = Array.prototype.slice.call(wrapper.children);

    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      if (isThemeOwn(child)) continue;
      if (!mayMove(child)) continue;

      /* Styled here rather than in custom-styles.liquid because the element has
         no stable class of its own - it is identified by position, not name. */
      child.style.maxWidth = '100%';
      child.style.marginTop = '20px';
      child.setAttribute('data-placed-below-options', 'true');

      anchor.insertAdjacentElement('afterend', child);
      anchor = child;
    }
  }

  function placeWishlist(root) {
    var wishlist = root.querySelector('.Vtl-WishlistButtonWrapper');
    if (!wishlist) return;

    var buyButtons = root.querySelector('.product-buy-buttons--root');
    if (!buyButtons) return;

    /* Direct child only. The mobile sticky drawer holds a second button with
       this same class, and the heart must not end up next to that one. */
    var addToCart = buyButtons.querySelector(':scope > .product-buy-buttons--primary');
    if (!addToCart) return;

    if (wishlist.parentElement === buyButtons && wishlist.previousElementSibling === addToCart) return;
    if (!mayMove(wishlist)) return;

    addToCart.insertAdjacentElement('afterend', wishlist);
  }

  /*
   * 9th October 2026 - THE TRUST BADGES.
   *
   * Vitals drops .vtl-tb-main-widget into the product form immediately after
   * the description block, which lands it in the middle of the accordion:
   *
   *   .product-form--block[data-block-type="description"]  data-fit-together="true"
   *   .vtl-tb-main-widget                                  <- here
   *   .product-form--block[data-block-type="collapsible-row"]  x4
   *
   * Two things are wrong with that. The client asked for the badges to sit
   * below the information rows rather than above them. And the theme joins
   * consecutive data-fit-together blocks into one continuous accordion, so a
   * foreign element in the middle splits Description away from MATERIALS and
   * the four rows below it - the join is by adjacency, and the badges break it.
   *
   * Moving the badges to after the LAST .product-form--block fixes both at
   * once: the fit-together blocks become siblings again and close up into a
   * single accordion, and the badges end up underneath it.
   *
   * Anchored on the last block rather than appended to the form, because a
   * form can carry trailing hidden inputs and this keeps the badges with the
   * visible content instead of after them.
   *
   * The class name is not guessed - it was read off the rendered page. Only
   * this one widget is touched; the stock countdown and the back-in-stock
   * button stay exactly where Vitals puts them.
   */
  function placeTrustBadges(root) {
    var form = root.querySelector('form.product-form');
    if (!form) return;

    var badges = form.querySelector(':scope > .vtl-tb-main-widget');
    if (!badges) return;

    var blocks = form.querySelectorAll(':scope > .product-form--block');
    if (!blocks.length) return;

    var last = blocks[blocks.length - 1];
    if (last === badges) return;
    if (badges.previousElementSibling === last) return;
    if (!mayMove(badges)) return;

    last.insertAdjacentElement('afterend', badges);
  }

  function place(root) {
    var wrapper = root.querySelector('.main-product--wrapper');
    if (wrapper) placeStrays(wrapper);
    placeWishlist(root);
    placeTrustBadges(root);
  }

  function watch(root) {
    place(root);

    var queued = false;

    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;

      window.requestAnimationFrame(function () {
        queued = false;
        place(root);
      });
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    var roots = document.querySelectorAll('.main-product--root');
    for (var i = 0; i < roots.length; i++) watch(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
